'use client';

import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {
  useGetTargetStrategyHistoryQuery,
  useGetEngageStrategyHistoryQuery,
} from 'src/api/redux';
import type { StrategyHistoryEntry } from 'src/api/redux/strategy/types';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faCheck,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { CircularProgress } from 'src/components';
import { PulseButton } from 'src/components/PulseUI';

type StrategyMode = 'target' | 'engage';

interface StrategyHistoryProps {
  strategyId?: string;
  strategyMode?: StrategyMode;
  onVersionSelect?: (historyEntry: StrategyHistoryEntry) => void;
}

const StrategyHistory: React.FC<StrategyHistoryProps> = ({
  strategyId,
  strategyMode = 'target',
  onVersionSelect,
}) => {
  const targetHistory = useGetTargetStrategyHistoryQuery(
    { strategyId: strategyId },
    {
      skip: !strategyId || strategyMode !== 'target',
    }
  );

  const engageHistory = useGetEngageStrategyHistoryQuery(
    { strategyId: strategyId },
    {
      skip: !strategyId || strategyMode !== 'engage',
    }
  );

  const { data, isLoading } =
    strategyMode === 'target' ? targetHistory : engageHistory;

  const versions = useMemo(() => {
    if (!data?.results) return [];

    // Filter for READY status and sort by change_date ascending (oldest first)
    const readyVersions = data.results
      .filter((item: StrategyHistoryEntry) => item.status === 'READY')
      .sort(
        (a: StrategyHistoryEntry, b: StrategyHistoryEntry) =>
          new Date(a.change_date).getTime() - new Date(b.change_date).getTime()
      );

    // Separate current from historical versions
    const historicalVersions = readyVersions.filter(
      (item: StrategyHistoryEntry) => !item.is_current
    );
    const currentVersion = readyVersions.find(
      (item: StrategyHistoryEntry) => item.is_current
    );

    // Check if current matches the latest historical version
    const latestHistorical = historicalVersions[historicalVersions.length - 1];
    const currentMatchesLatest =
      currentVersion &&
      latestHistorical &&
      JSON.stringify(currentVersion.template) ===
        JSON.stringify(latestHistorical.template);

    // Number historical versions v1, v2, etc.
    // If current matches latest, label the last one as "Current" instead
    const labeled = historicalVersions.map(
      (item: StrategyHistoryEntry, index: number) => {
        const isLast = index === historicalVersions.length - 1;
        return {
          ...item,
          versionLabel:
            isLast && currentMatchesLatest ? 'Current' : `v${index + 1}`,
          versionNumber: index + 1,
          // Mark as current if it matches
          is_current: isLast && currentMatchesLatest ? true : item.is_current,
        };
      }
    );

    // Only add current separately if it doesn't match the latest historical
    if (currentVersion && !currentMatchesLatest) {
      labeled.push({
        ...currentVersion,
        versionLabel: 'Current',
        versionNumber: historicalVersions.length + 1,
      });
    }

    return labeled;
  }, [data]);

  const [selectedHistoryId, setSelectedHistoryId] = React.useState<
    number | null
  >(null);

  // Set initial selection to current version
  React.useEffect(() => {
    const currentVersion = versions.find(
      (v: StrategyHistoryEntry & { versionLabel: string }) => v.is_current
    );
    if (currentVersion && selectedHistoryId === null) {
      setSelectedHistoryId(currentVersion.history_id);
    }
  }, [versions, selectedHistoryId]);

  const handleVersionClick = (
    version: StrategyHistoryEntry & { versionLabel: string }
  ) => {
    setSelectedHistoryId(version.history_id);
    onVersionSelect?.(version);
  };

  // Scroll functionality
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  const scrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: -100, behavior: 'smooth' });
  }, []);

  const scrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: 100, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Small delay to ensure DOM has rendered
    const timer = setTimeout(() => {
      checkScrollPosition();
    }, 100);

    container.addEventListener('scroll', checkScrollPosition);
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      clearTimeout(timer);
      container.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [checkScrollPosition, versions]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div
        className="w-full h-8 bg-neutral-50 border border-neutral-200 rounded-full flex items-center justify-center"
        data-testid="strategy-history-loading"
      >
        <CircularProgress size="sm" color="neutral" />
      </div>
    );
  }

  if (versions.length === 0 && !isLoading) {
    return (
      <div
        className="w-full h-8 bg-neutral-50 border border-neutral-200 rounded-full flex items-center justify-center px-3"
        data-testid="strategy-history-no-versions"
      >
        <span className="text-xs text-tertiary">
          No version history available
        </span>
      </div>
    );
  }

  return (
    <div
      className="w-full bg-neutral-50 border border-neutral-200 rounded-full px-3 py-1.5 flex items-center gap-2"
      data-testid="strategy-history"
      aria-label="Strategy version history"
    >
      <div className="flex items-center gap-1.5 text-tertiary">
        <Icon icon={faHistory} className="w-3 h-3" aria-hidden="true" />
        <span className="text-xs font-medium">History</span>
      </div>
      <div className="h-4 w-px bg-neutral-300" aria-hidden="true" />

      <div className="relative flex-1 flex items-center overflow-hidden min-w-0">
        {/* Left scroll arrow */}
        {canScrollLeft && (
          <button
            onClick={scrollLeft}
            className="absolute left-0 z-10 flex items-center justify-center w-8 h-full bg-gradient-to-r from-neutral-50 via-neutral-50 to-transparent cursor-pointer"
            aria-label="Scroll left"
            data-testid="strategy-history-scroll-left"
          >
            <Icon icon={faChevronLeft} className="w-3 h-3 text-neutral-500" />
          </button>
        )}

        <div
          ref={scrollContainerRef}
          className="flex items-center gap-1.5 overflow-x-auto flex-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          role="tablist"
          aria-label="Version history"
          data-testid="strategy-history-versions"
        >
          {versions.map(
            (version: StrategyHistoryEntry & { versionLabel: string }) => {
              const isSelected = selectedHistoryId === version.history_id;
              return (
                <PulseButton
                  key={version.history_id}
                  onClick={() => handleVersionClick(version)}
                  variant={isSelected ? 'solid' : 'outline'}
                  color={isSelected ? 'primary' : 'neutral'}
                  size="xs"
                  startDecorator={
                    version.is_current && isSelected ? faCheck : undefined
                  }
                  aria-pressed={isSelected}
                  aria-label={`${version.versionLabel} - ${formatDate(
                    version.change_date
                  )}`}
                  data-testid={`strategy-history-version-${version.versionLabel
                    .toLowerCase()
                    .replace(' ', '-')}`}
                  className="flex-shrink-0"
                >
                  <span>{version.versionLabel}</span>
                  {!version.is_current && (
                    <span>{formatDate(version.change_date)}</span>
                  )}
                </PulseButton>
              );
            }
          )}
        </div>

        {/* Right scroll arrow */}
        {canScrollRight && (
          <button
            onClick={scrollRight}
            className="absolute right-0 z-10 flex items-center justify-center w-8 h-full bg-gradient-to-l from-neutral-50 via-neutral-50 to-transparent cursor-pointer"
            aria-label="Scroll right"
            data-testid="strategy-history-scroll-right"
          >
            <Icon icon={faChevronRight} className="w-3 h-3 text-neutral-500" />
          </button>
        )}
      </div>
    </div>
  );
};

export default StrategyHistory;
